import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ApiEnvelope,
  ApiPublicUser,
  ApiUserRole,
  AuthResultDto,
  TwoFactorChallengeDto,
} from '../models/auth-api.model';
import { ApiRecordStatus } from '../models/api.model';
import { AuthSession, LoginCredentials, RegisterPayload, User } from '../models/user.model';
import { ROLE_HOME, UserRole } from '../models/user-role.enum';
import { httpErrorMessage } from '../http-error-message';
import { SessionService } from './session.service';
import { ClientDeviceIdService } from './client-device-id.service';
import { CourseContextService } from './course-context.service';
import { CommunicationSocketService } from './communication-socket.service';
import { MessagingStore } from './messaging.store';
import {
  PromotionCodeApiService,
  RedeemPromotionCodeResult,
} from './promotion-code-api.service';

/** Auth against brandon.api (`api/v1/identity/auth`). */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(SessionService);
  private readonly router = inject(Router);
  private readonly chatSocket = inject(CommunicationSocketService);
  private readonly messaging = inject(MessagingStore);
  private readonly promotionCodes = inject(PromotionCodeApiService);
  private readonly deviceId = inject(ClientDeviceIdService);
  private readonly courseContext = inject(CourseContextService);

  private readonly authUrl = `${environment.apiUrl}/identity/auth`;
  private refreshInFlight: Observable<AuthSession> | null = null;

  readonly user = this.session.user;
  readonly isAuthenticated = this.session.isAuthenticated;

  /** Students/teachers who still need a promotion code before using the panel. */
  needsActivation(): boolean {
    const user = this.session.user();
    if (!user) return false;
    if (user.role === UserRole.Admin) return false;
    return (
      user.status === ApiRecordStatus.INACTIVE ||
      user.status === ApiRecordStatus.ARCHIVED
    );
  }

  login(credentials: LoginCredentials): Observable<AuthSession> {
    if (!credentials.email || !credentials.password) {
      return throwError(() => new Error('Enter your email and password to continue.'));
    }
    return from(this.deviceId.resolve()).pipe(
      switchMap((clientId) =>
        this.http.post<ApiEnvelope<AuthResultDto | TwoFactorChallengeDto>>(
          `${this.authUrl}/login`,
          {
            email: credentials.email,
            password: credentials.password,
            deviceType: 'web',
            clientId,
          },
        ),
      ),
      map(({ data: result }) => {
        if ('requiresTwoFactor' in result) {
          // TODO: OTP screen — until it exists, 2FA accounts cannot sign in here.
          throw new Error('This account uses a two-factor code, which this app cannot collect yet.');
        }
        this.applyCurrentCourse(result);
        return toSession(result);
      }),
      tap((session) => this.onAuthenticated(session)),
      catchError((error) =>
        throwError(() => friendly(error, 'We could not sign you in. Check your details and try again.')),
      ),
    );
  }

  register(payload: RegisterPayload): Observable<AuthSession> {
    return this.http
      .post<ApiEnvelope<AuthResultDto>>(`${this.authUrl}/register`, {
        email: payload.email,
        password: payload.password,
        // The API keeps a single display name; learningLanguage has no home there yet.
        name: `${payload.firstName} ${payload.lastName}`.trim(),
        roles: [toApiRole(payload.role)],
      })
      .pipe(
        map(({ data }) => {
          this.applyCurrentCourse(data);
          return toSession(data);
        }),
        tap((session) => this.onAuthenticated(session)),
        catchError((error) =>
          throwError(() => friendly(error, 'We could not create your account. Try again.')),
        ),
      );
  }

  requestPasswordReset(email: string): Observable<void> {
    if (!email) {
      return throwError(() => new Error('Enter the email you signed up with.'));
    }
    return this.http.post(`${this.authUrl}/forgot-password`, { email }).pipe(
      map(() => void 0),
      catchError((error) =>
        throwError(() => friendly(error, 'We could not send the reset email. Try again.')),
      ),
    );
  }

  resetPassword(token: string, password: string): Observable<void> {
    if (password.length < 8) {
      return throwError(() => new Error('Use at least 8 characters.'));
    }
    return this.http.post(`${this.authUrl}/reset-password`, { token, newPassword: password }).pipe(
      map(() => void 0),
      catchError((error) =>
        throwError(() => friendly(error, 'That reset link no longer works. Request a new one.')),
      ),
    );
  }

  /** Exchange the stored refresh token for a new access + refresh pair (rotation). */
  refreshSession(): Observable<AuthSession> {
    const refreshToken = this.session.refreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }

    if (!this.refreshInFlight) {
      this.refreshInFlight = this.http
        .post<ApiEnvelope<AuthResultDto>>(`${this.authUrl}/refresh`, { refreshToken })
        .pipe(
          map(({ data }) => {
            this.applyCurrentCourse(data);
            return toSession(data);
          }),
          tap((session) => this.onAuthenticated(session)),
          catchError((error) => throwError(() => error)),
          finalize(() => {
            this.refreshInFlight = null;
          }),
          shareReplay(1),
        );
    }

    return this.refreshInFlight;
  }

  logout(): void {
    // Best-effort audit call; locally the session is gone either way.
    if (this.session.token()) {
      this.http
        .post(`${this.authUrl}/logout`, {})
        .pipe(catchError(() => of(null)))
        .subscribe();
    }
    this.onSignedOut();
    void this.router.navigate(['/auth/login']);
  }

  /** Where this user belongs after a successful sign-in. */
  homeRoute(): string {
    const role = this.session.role() ?? UserRole.Student;
    return ROLE_HOME[role];
  }

  /**
   * Post-auth landing: inactive students/teachers must enter a promotion code
   * before reaching their dashboard.
   */
  postAuthRoute(returnUrl?: string | null): string {
    if (this.needsActivation()) {
      return '/auth/activate';
    }
    return returnUrl || this.homeRoute();
  }

  /** Redeem a promotion code for the signed-in student/teacher, then refresh session. */
  redeemPromotionCode(code: string): Observable<RedeemPromotionCodeResult> {
    return this.promotionCodes.redeemForMe(code.trim()).pipe(
      tap((result) => {
        const current = this.session.user();
        if (!current) return;
        this.session.updateUser({
          ...current,
          status: ApiRecordStatus.ACTIVE,
        });
        if (result.auth) {
          this.onAuthenticated(toSession(result.auth));
        }
      }),
      catchError((error) =>
        throwError(() => friendly(error, 'That promotion code did not work. Try again.')),
      ),
    );
  }

  private onAuthenticated(session: AuthSession): void {
    this.session.set(session);
    this.chatSocket.connect(session.token);
  }

  private onSignedOut(): void {
    this.chatSocket.disconnect();
    this.messaging.reset();
    this.session.clear();
  }

  private applyCurrentCourse(result: AuthResultDto): void {
    const courseId = result.currentCourse?.curriculumCourseId;
    if (courseId) {
      this.courseContext.preferCourse(courseId);
    }
  }
}

function toSession(result: AuthResultDto): AuthSession {
  return {
    token: result.accessToken,
    refreshToken: result.refreshToken,
    user: toUser(result.user),
  };
}

/** The API keeps one display name and numeric roles; the UI wants more. */
export function toUser(user: ApiPublicUser): User {
  const name = user.name?.trim() || user.email.split('@')[0];
  const [firstName, ...rest] = name.split(/\s+/);
  const lastName = rest.join(' ');
  const role = toRole(user.roles);
  return {
    id: user.id,
    firstName,
    lastName,
    email: user.email,
    role,
    status: user.status ?? ApiRecordStatus.ACTIVE,
    initials: `${firstName[0] ?? ''}${lastName[0] ?? firstName[1] ?? ''}`.toUpperCase(),
    avatarUrl: user.avatarUrl || undefined,
    caption: role === UserRole.Teacher ? 'Teacher' : role === UserRole.Admin ? 'Admin' : 'Learner',
  };
}

function toRole(roles: ApiUserRole[]): UserRole {
  if (roles.includes(ApiUserRole.Administrator)) return UserRole.Admin;
  if (roles.includes(ApiUserRole.Teacher)) return UserRole.Teacher;
  return UserRole.Student;
}

function toApiRole(role: UserRole.Student | UserRole.Teacher): ApiUserRole {
  return role === UserRole.Teacher ? ApiUserRole.Teacher : ApiUserRole.Student;
}

/** Surface the API's validation message when there is one; keep the copy warm otherwise. */
function friendly(error: unknown, fallback: string): Error {
  return new Error(httpErrorMessage(error, fallback));
}
